/**
 * Startup Insights Client Types
 *
 * Type definitions for the Startup Insights UI components.
 */

import type { InsightsMessage, InsightsStartupContext, InsightsFocusArea, InsightsActionItem } from '@/lib/supabase/edge-functions';

// Re-export base types
export type {
  InsightsMessage,
  InsightsRole,
  InsightsStartupContext,
  InsightsFocusArea,
  InsightsActionItem,
} from '@/lib/supabase/edge-functions';

// =============================================================================
// UI State Types
// =============================================================================

export interface InsightsUIState {
  /** Whether the insights panel is visible */
  isOpen: boolean;
  /** Whether the panel is expanded */
  isExpanded: boolean;
  /** Current input value */
  inputValue: string;
  /** Current focus area */
  focusArea: InsightsFocusArea | null;
}

// =============================================================================
// Component Props Types
// =============================================================================

export interface InsightsMessageProps {
  /** The message to display */
  message: InsightsMessage;
  /** Whether this is the last message */
  isLast?: boolean;
  /** Whether the message is being generated */
  isStreaming?: boolean;
  /** Action items in this message */
  actionItems?: string[];
  /** Callback when user taps a suggested follow-up */
  onSuggestionTap?: (suggestion: string) => void;
}

export interface InsightsInputProps {
  /** Current input value */
  value: string;
  /** Callback when value changes */
  onChangeText: (text: string) => void;
  /** Callback when user submits */
  onSubmit: () => void;
  /** Whether submission is disabled */
  disabled?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Whether to auto-focus */
  autoFocus?: boolean;
}

export interface InsightsContainerProps {
  /** Startup context for the insights */
  startupContext: InsightsStartupContext;
  /** Startup ID (for tracking) */
  startupId?: string;
  /** Whether to show in compact mode initially */
  initialCompact?: boolean;
  /** Callback when panel is closed */
  onClose?: () => void;
  /** Callback when action items are updated */
  onActionItemsChange?: (items: InsightsActionItem[]) => void;
  /**
   * How this conversation is being presented.
   *
   * 'page'  — it IS the screen. The page frame owns the title and the way out,
   *           so the panel draws neither a close button nor a minimise control,
   *           and always fills its slot.
   * 'panel' — it is a window inside a host screen, and keeps its window chrome.
   *
   * Defaults to 'panel' so an existing embedded consumer is unaffected.
   */
  presentation?: 'page' | 'panel';
}

// =============================================================================
// Quick Actions
// =============================================================================

export interface InsightsQuickAction {
  id: string;
  label: string;
  icon: string;
  prompt: string;
  focusArea: InsightsFocusArea;
  description: string;
}

export const INSIGHTS_QUICK_ACTIONS: InsightsQuickAction[] = [
  {
    id: 'score',
    label: 'Improve Score',
    icon: 'trending-up',
    prompt: 'How can I improve my Inverio Score rating? What are the highest-impact actions I can take?',
    focusArea: 'score-improvement',
    description: 'Get specific tips to boost your score',
  },
  {
    id: 'gaps',
    label: 'Gap Analysis',
    icon: 'alert-circle',
    prompt: 'What are my biggest gaps compared to what investors expect at my stage?',
    focusArea: 'gap-analysis',
    description: 'Identify weaknesses to address',
  },
  {
    id: 'investor',
    label: 'Investor Prep',
    icon: 'users',
    prompt: 'What tough questions should I prepare for when meeting investors?',
    focusArea: 'investor-prep',
    description: 'Practice handling investor questions',
  },
  {
    id: 'pitch',
    label: 'Pitch Practice',
    icon: 'mic',
    prompt: 'Help me practice my pitch. What story should I tell about my startup?',
    focusArea: 'pitch-practice',
    description: 'Craft your compelling narrative',
  },
  {
    id: 'traction',
    label: 'Grow Traction',
    icon: 'bar-chart-2',
    prompt: 'What should I focus on to improve my traction metrics?',
    focusArea: 'traction-growth',
    description: 'Strategies to improve metrics',
  },
  {
    id: 'fundraising',
    label: 'Fundraising',
    icon: 'dollar-sign',
    prompt: 'Am I ready to raise? What should I prepare before approaching investors?',
    focusArea: 'fundraising',
    description: 'Get fundraising-ready',
  },
];

// =============================================================================
// Action Item Categories
// =============================================================================

export interface ActionItemCategory {
  id: string;
  label: string;
  icon: string;
  color: string;
}

export const ACTION_ITEM_CATEGORIES: ActionItemCategory[] = [
  { id: 'documentation', label: 'Documentation', icon: 'file-text', color: 'blue' },
  { id: 'metrics', label: 'Metrics', icon: 'bar-chart-2', color: 'green' },
  { id: 'team', label: 'Team', icon: 'users', color: 'purple' },
  { id: 'product', label: 'Product', icon: 'box', color: 'orange' },
  { id: 'market', label: 'Market', icon: 'globe', color: 'cyan' },
  { id: 'fundraising', label: 'Fundraising', icon: 'dollar-sign', color: 'yellow' },
];
